import _, {isString} from 'lodash';
import memoize from 'lru-memoize';

import {addQueryToPath} from '../../libs/shared/helpers/src/browser/urlHelpers';
import {CancelToken} from '../helpers/asyncCancelHelpers';
import {doesTeammateMatchQuery} from '../helpers/modelHelpers';
import {CompanyModel} from '../models/api/companyModel';
import {ApiRecipientContactMatches, RecipientContactMatchModel} from '../models/api/recipientMatchModel';
import {TeammateModel} from '../models/api/teammateModel';
import {fetchApiAsync} from './http/http';

interface FetchRecipientContactMatchesOptions {
  limit?: number;
  excludeEmails?: ReadonlySet<string>;
  cancelToken?: CancelToken;
  preferredNamespace?: string;
}

export async function fetchRecipientContactMatchesAsync(
  companyUrl: string,
  searchQuery: string,
  options: FetchRecipientContactMatchesOptions = {},
) {
  const {limit, excludeEmails, cancelToken, preferredNamespace} = options;

  // Perform the query.
  const encodedSearchQuery = encodeURIComponent(searchQuery.toLowerCase());
  const path = addQueryToPath(`${companyUrl}/search_contact2/email/${encodedSearchQuery}`, {
    namespace: preferredNamespace,
  });
  const results = await fetchApiAsync(ApiRecipientContactMatches, path, {cancelToken});

  // Filter if needed.
  return _(results)
    .uniqBy((c) => c.handle)
    .reject((c) => Boolean(excludeEmails && excludeEmails.has(c.handle)))
    .take(limit || Number.MAX_SAFE_INTEGER)
    .value();
}

/*
 * Constants.
 */

const resultsLimit = 7;
const maxContactResults = 4;

/*
 * Types.
 */

export type InviteTracker = TeammateModel | string;

/*
 * API.
 */

export async function searchParticipantsAsync(
  searchText: string,
  trackers: ReadonlyArray<InviteTracker>,
  teammates: ReadonlyArray<TeammateModel>,
  company: CompanyModel,
  mentionableEmails: Set<string>,
  canAutocompleteMentionContacts: boolean,
  cancelToken?: CancelToken,
): Promise<ReadonlyArray<TeammateModel | RecipientContactMatchModel>> {
  const query = searchText.trim();
  if (!query) {
    return [];
  }

  // If teammates are enough, return early.
  const filteredTeammates = filterInvitedTeammates(teammates, trackers, query, resultsLimit);

  // Check if we have enough teammates, or if we need contacts on top.
  if (filteredTeammates.length >= resultsLimit || !canAutocompleteMentionContacts) {
    return filteredTeammates;
  }

  // List the email trackers.
  const visitorTrackers = trackers.filter(isString);

  // Otherwise, fetch additional contacts.

  // Maybe update the collection of emails to exclude.
  const updatedExcludedEmails = new Set([...mentionableEmails, ...visitorTrackers]);

  // Fetch enough to reach the overall limit.
  const contactResultsLimit = Math.min(maxContactResults, resultsLimit - filteredTeammates.length);
  const contacts = await fetchRecipientContactMatchesAsync(company.url, query, {
    limit: contactResultsLimit,
    excludeEmails: updatedExcludedEmails,
    cancelToken,
  });
  return [...filteredTeammates, ...contacts];
}

export function buildRecipientContactMatchSearchUrl(
  company: CompanyModel,
  query: string,
  options: FetchRecipientContactMatchesOptions = {},
) {
  const {preferredNamespace} = options;

  return addQueryToPath(`${company.url}/search_contact2/email/${encodeURIComponent(query.toLowerCase())}`, {
    namespace: preferredNamespace,
  });
}

/*
 * Helper.
 */

const filterInvitedTeammates = memoize(20)(
  (
    teammates: ReadonlyArray<TeammateModel>,
    trackers: ReadonlyArray<InviteTracker>,
    query: string,
    limit: number,
  ) => {
    // Shortcut for empty query.
    if (!query) {
      return teammates;
    }

    // Otherwise, filter by name.
    return _(teammates)
      .filter((tm) => !trackers.includes(tm) && doesTeammateMatchQuery(tm, query))
      .take(limit)
      .value();
  },
);
